// `UntrustedText` — words the office did not write: an agent's note, the body
// an agent proposes to send, a customer's message quoted back. Spec 14 §4.5
// gives such text one frame, and D-516 ruling 4 forbids styling an agent's
// note as staff-authored text. One primitive keeps the frame identical on the
// journal card, in the quote composer and wherever it is needed next, so a
// reader learns it once.
//
// The frame is a `<figure>` with a dashed rule and a caption that says whose
// words these are and what to do with them. The text keeps its line breaks
// and wraps anywhere, so a long token cannot push the card wider than a phone.
// `data-untrusted` marks it for the walkthroughs, which assert the agent's
// words never render outside it.
//
// i18n-free like the other primitives: the caller passes both caption parts.

export interface UntrustedTextProps {
  /** Whose words these are, e.g. "Agent's note". */
  label: string;
  /** What the reader should do with them, e.g. "Written by the agent. Check it before you act." */
  frameNote: string;
  text: string;
  /** The language the words are in (BCP 47), when known: an agent's note
   * records its locale, so a screen reader reads it with the right voice. */
  lang?: string | undefined;
  "data-testid"?: string;
}

export function UntrustedText({ label, frameNote, text, lang, ...rest }: UntrustedTextProps) {
  return (
    <figure className="ds-untrusted" data-testid={rest["data-testid"]} data-untrusted="true">
      <figcaption className="ds-untrusted-label">
        {label} · {frameNote}
      </figcaption>
      <p className="ds-untrusted-text" lang={lang}>
        {text}
      </p>
    </figure>
  );
}
